import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '../store';
import { getOrders } from '../firebase/database';
import { logoutUser } from '../firebase/auth';
import { updateProfile, updatePassword } from 'firebase/auth';
import { update, ref } from 'firebase/database';
import { db, auth } from '../firebase/config';
import { uploadImage } from '../firebase/storage';
import { compressImage } from '../utils/imageCompress';
import { formatPrice, formatDate, STATUS_COLORS } from '../utils/helpers';
import SEO from '../components/common/SEO';
import { Spinner } from '../components/common/Loader';
import toast from 'react-hot-toast';

const AccountLayout = () => {
  const { user, clearUser } = useAuthStore();
  const location = useLocation();
  if (!user) return <Navigate to="/login"/>;

  const handleLogout = async () => { await logoutUser(); clearUser(); toast.success('Logged out'); };
  const navItems = [
    { to: '/account', label: 'Profile', exact: true, icon: 'user' },
    { to: '/account/orders', label: 'My Orders', icon: 'orders' },
    { to: '/wishlist', label: 'Wishlist', icon: 'heart' },
    { to: '/account/settings', label: 'Password & Security', icon: 'lock' },
  ];

  const Icon = ({ name }) => {
    const icons = {
      user: <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>,
      orders: <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>,
      heart: <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>,
      lock: <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>,
    };
    return <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">{icons[name]}</svg>;
  };

  return (
    <div className="min-h-screen bg-[#f8f9fc]">
      <div className="bg-[#0f2347] py-10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 bg-[#e63946] text-white flex items-center justify-center text-2xl font-black">
              {user.photoURL ? <img src={user.photoURL} alt="" className="w-full h-full object-cover"/> : (user.displayName?.[0]?.toUpperCase()||'U')}
            </div>
            <div>
              <h1 className="font-display text-2xl font-black text-white">{user.displayName||'My Account'}</h1>
              <p className="text-white/60 text-sm">{user.email}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          <aside className="space-y-1">
            {navItems.map(({ to, label, exact, icon }) => (
              <Link key={to} to={to}
                className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all duration-200 rounded-xl ${(exact ? location.pathname===to : location.pathname.startsWith(to)) ? 'bg-[#1a3a6e] text-white shadow-md' : 'text-gray-600 hover:bg-gray-100 hover:text-[#1a3a6e]'}`}>
                <Icon name={icon}/> {label}
              </Link>
            ))}
            <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-red-500 hover:bg-red-50 w-full rounded-xl transition-all">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>
              Logout
            </button>
          </aside>
          <div className="lg:col-span-3">
            <Routes>
              <Route path="/" element={<Profile/>}/>
              <Route path="/orders" element={<MyOrders/>}/>
              <Route path="/settings" element={<Settings/>}/>
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── PROFILE — photo, name, phone all editable ────────────────────────────────
const Profile = () => {
  const { user, userData, setUserData, setUser } = useAuthStore();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: user?.displayName||'', phone: userData?.phone||'', address: userData?.address||'', city: userData?.city||'' });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(user?.photoURL||'');
  const [saving, setSaving] = useState(false);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) { setPhotoFile(file); setPhotoPreview(URL.createObjectURL(file)); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let photoURL = user?.photoURL || '';
      if (photoFile) {
        const compressed = await compressImage(photoFile, 400, 0.8);
        photoURL = await uploadImage(compressed, `users/${user.uid}/avatar_${Date.now()}.jpg`);
      }
      await updateProfile(auth.currentUser, { displayName: form.name, photoURL });
      await update(ref(db, `users/${user.uid}`), { name: form.name, phone: form.phone, address: form.address, city: form.city, photoURL });
      setUserData({ ...userData, name: form.name, phone: form.phone, address: form.address, city: form.city, photoURL });
      setUser({ ...auth.currentUser });
      setEditing(false);
      toast.success('Profile updated!');
    } catch (err) { toast.error('Failed to update: ' + err.message); }
    finally { setSaving(false); }
  };

  return (
    <>
      <SEO title="My Profile"/>
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-bold text-lg text-gray-900">Profile Information</h2>
          {!editing && (
            <button onClick={() => setEditing(true)}
              className="flex items-center gap-1.5 text-sm font-semibold text-[#1a3a6e] hover:text-[#e63946] transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
              Edit Profile
            </button>
          )}
        </div>

        {editing ? (
          <div className="max-w-md space-y-5">
            {/* Photo upload */}
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center text-2xl font-black text-gray-400">
                  {photoPreview ? <img src={photoPreview} alt="" className="w-full h-full object-cover"/> : (form.name?.[0]?.toUpperCase()||'U')}
                </div>
                <label className="absolute -bottom-1 -right-1 w-7 h-7 bg-[#1a3a6e] rounded-full flex items-center justify-center cursor-pointer hover:bg-[#e63946] transition-colors shadow-md">
                  <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/><path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                  <input type="file" accept="image/*" className="hidden" onChange={handlePhotoChange}/>
                </label>
              </div>
              <p className="text-xs text-gray-400">Click the icon to change your profile photo</p>
            </div>
            <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Full Name</label>
              <input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} className="input-field"/></div>
            <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Phone Number</label>
              <input value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} className="input-field" placeholder="0300-0000000"/></div>
            <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Address</label>
              <input value={form.address} onChange={e=>setForm(f=>({...f,address:e.target.value}))} className="input-field" placeholder="Street, area"/></div>
            <div><label className="block text-xs font-bold text-gray-700 mb-1.5">City</label>
              <input value={form.city} onChange={e=>setForm(f=>({...f,city:e.target.value}))} className="input-field" placeholder="Karachi"/></div>
            <div className="flex gap-3">
              <button onClick={handleSave} disabled={saving} className="btn-primary text-sm py-2.5 flex items-center gap-2">{saving&&<Spinner size="sm" color="white"/>}Save Changes</button>
              <button onClick={()=>setEditing(false)} className="btn-outline text-sm py-2.5">Cancel</button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-[#1a3a6e] flex items-center justify-center text-2xl font-black text-white flex-shrink-0">
                {user?.photoURL ? <img src={user.photoURL} alt="" className="w-full h-full object-cover"/> : (user?.displayName?.[0]?.toUpperCase()||'U')}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { label:'Full Name', value:user?.displayName||'—' },
                { label:'Email Address', value:user?.email },
                { label:'Phone', value:userData?.phone||'Not set' },
                { label:'City', value:userData?.city||'Not set' },
                { label:'Address', value:userData?.address||'Not set' },
                { label:'Member Since', value:formatDate(userData?.createdAt) },
              ].map(({ label, value }) => (
                <div key={label}>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
                  <p className="text-gray-900 font-medium">{value}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const MyOrders = () => {
  const { user } = useAuthStore();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { getOrders(user.uid).then(setOrders).finally(()=>setLoading(false)); }, [user.uid]);

  return (
    <>
      <SEO title="My Orders"/>
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h2 className="font-bold text-lg text-gray-900 mb-6">My Orders</h2>
        {loading ? <div className="flex justify-center py-12"><Spinner/></div>
        : orders.length===0 ? (
          <div className="text-center py-12 text-gray-500">
            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>
            <p>No orders yet</p>
            <Link to="/shop" className="btn-primary inline-block mt-4 text-sm">Start Shopping</Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map(order => (
              <div key={order.id} className="border border-gray-100 p-4 hover:border-gray-200 transition-colors rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-bold text-sm text-gray-900">#{order.orderId||order.id?.slice(0,8).toUpperCase()}</p>
                    <p className="text-xs text-gray-500">{formatDate(order.createdAt)}</p>
                  </div>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full capitalize ${STATUS_COLORS[order.status]||'bg-gray-100 text-gray-600'}`}>{order.status}</span>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  {order.items?.slice(0,3).map((item,i) => (
                    <img key={i} src={item.image||'https://via.placeholder.com/40'} alt={item.title} className="w-10 h-10 object-cover rounded" onError={e=>e.target.src='https://via.placeholder.com/40'}/>
                  ))}
                  {order.items?.length>3&&<span className="text-xs text-gray-500">+{order.items.length-3} more</span>}
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-500">{order.items?.length} item(s) · {order.paymentMethod==='cod'?'Cash on Delivery':order.paymentMethod}</p>
                  <p className="font-black text-[#1a3a6e]">{formatPrice(order.total)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

const Settings = () => {
  const [form, setForm] = useState({ newPass:'', confirm:'' });
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (form.newPass!==form.confirm) { toast.error('Passwords do not match'); return; }
    if (form.newPass.length<6) { toast.error('Min 6 characters'); return; }
    setSaving(true);
    try {
      await updatePassword(auth.currentUser, form.newPass);
      setForm({ newPass:'', confirm:'' });
      toast.success('Password updated!');
    } catch { toast.error('Failed. Please log in again and retry.'); }
    finally { setSaving(false); }
  };

  return (
    <>
      <SEO title="Account Settings"/>
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h2 className="font-bold text-lg text-gray-900 mb-2">Change Password</h2>
        <p className="text-gray-400 text-sm mb-6">Update your account password for better security</p>
        <form onSubmit={handleUpdate} className="space-y-4 max-w-md">
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">New Password</label>
            <div className="relative">
              <input type={show?'text':'password'} value={form.newPass} onChange={e=>setForm(f=>({...f,newPass:e.target.value}))} className="input-field pr-10" placeholder="Min 6 characters"/>
              <button type="button" onClick={()=>setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {show ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/></svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                )}
              </button>
            </div>
          </div>
          <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Confirm New Password</label>
            <input type={show?'text':'password'} value={form.confirm} onChange={e=>setForm(f=>({...f,confirm:e.target.value}))} className="input-field" placeholder="Repeat password"/></div>
          <button type="submit" disabled={saving} className="btn-primary text-sm flex items-center gap-2">{saving&&<Spinner size="sm" color="white"/>}Update Password</button>
        </form>
      </div>
    </>
  );
};

export default AccountLayout;
